import {
  ALL_TRANSLATION_HANDLERS_QUERY,
  TRANSLATION_HANDLER_BY_INTERNAL_NAME_QUERY,
} from '@src/gql/translation-handler-gql';
import { LogManager } from '@src/utility/Logger';

import type { TranslationHandlerRecord } from '@src/types.ts/translationHandlerRecord';
import type { GraphQLClient } from 'graphql-request';

// Resilience contract: if the translation_handler Dato model is absent from
// the target environment (migration not yet applied), GraphQL fails schema
// validation. We catch, log a warn, and return []. Callers treat that as
// "no handlers registered" and fall back to code-level defaults.
export class TranslationHandlerRepository {
  private logger = LogManager.getLogger(
    'repository:TranslationHandlerRepository',
  );
  private cached: TranslationHandlerRecord[] | null = null;

  constructor(private gqlClient: GraphQLClient) {}

  async getAllActiveHandlers(): Promise<TranslationHandlerRecord[]> {
    if (this.cached) return this.cached;
    try {
      const response = (await this.gqlClient.request(
        ALL_TRANSLATION_HANDLERS_QUERY,
      )) as { allTranslationHandlers: TranslationHandlerRecord[] };
      this.cached = response.allTranslationHandlers;
      return this.cached;
    } catch (err) {
      this.logger.warn(
        'Failed to load translation handlers; defaulting to empty. This is expected if the translation_handler Dato model has not been migrated yet.',
        err,
      );
      return [];
    }
  }

  async getHandlerByInternalName(
    internalName: string,
  ): Promise<TranslationHandlerRecord | null> {
    try {
      const response = (await this.gqlClient.request(
        TRANSLATION_HANDLER_BY_INTERNAL_NAME_QUERY,
        { internalName },
      )) as { translationHandler: TranslationHandlerRecord | null };
      return response.translationHandler;
    } catch (err) {
      this.logger.warn(
        'Failed to load translation handler by internal name; defaulting to null',
        { internalName, err },
      );
      return null;
    }
  }
}
